/// <amd-module name="Repos/FilesHubCommon/Components/FileDropTarget" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { css } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";

export interface IFileDropTargetProps {
    children?: React.ReactNode;
    className?: string;
    onDrop: (dataTransfer: DataTransfer) => void;
}

function isSafari(): boolean {
    return !!/Safari\/([\d.]+)/i.exec(window.navigator.userAgent) && navigator.userAgent.toLowerCase().indexOf("chrome") === -1;
}

export const FileDropTarget = (props: IFileDropTargetProps): JSX.Element => {
    const isDragOver = new ObservableValue(false);
    const ref = React.createRef<HTMLDivElement>();
    let dragEnterElement: Element;

    const setDropEffect = (dataTransfer: DataTransfer) => {
        let dropEffect: DataTransfer["dropEffect"] = "none";

        if (dataTransfer.items && dataTransfer.items.length) {
            const dataTransferItem = dataTransfer.items[0];
            if (
                dataTransferItem.kind === "file" ||
                dataTransfer.types[0] === "Files" || // IE
                dataTransfer.types[0] === "public.file-url"
            ) {
                // Safari
                dropEffect = "copy";
                if (isSafari()) {
                    dropEffect = "all" as any;
                }
            }
        } else if (dataTransfer.types && dataTransfer.types.length && dataTransfer.types[0] === "Files") {
            // IE may not carry an "items" property
            dropEffect = "copy";
        }

        try {
            dataTransfer.dropEffect = dropEffect;
        } catch {
            // Assigning dataTransfer.dropEffect may throw in Edge
        }

        return dropEffect !== "none";
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDropEffect(event.dataTransfer);
    };

    const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        if (setDropEffect(event.dataTransfer)) {
            event.preventDefault();
            event.stopPropagation();
            dragEnterElement = event.target as Element;
            if (!isDragOver.value) {
                isDragOver.value = true;
            }
        }
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (isDragOver.value) {
            if (dragEnterElement == event.target) {
                isDragOver.value = false;
            }
        }
    };

    const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        props.onDrop(event.dataTransfer);
        isDragOver.value = false;
    };

    return (
        <Observer isDragOver={isDragOver}>
            {(dragProps: { isDragOver: boolean }) => (
                <div
                    className={css(props.className, dragProps.isDragOver && "repos-file-drop-target-hover")}
                    onDragOver={onDragOver}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    ref={ref}
                >
                    {props.children}
                </div>
            )}
        </Observer>
    );
};
